import type {Values} from '@wix/devzai-utils-common';
import {DoppeProductsGridLayoutSize} from '../doppe-products-grid-layout-size';
import {DoppeActionLayoutMediaRatio} from '../doppe-action-layout';
import {DoppeRowTemplateButtonDesign} from '../doppe-row-template-button-design/doppe-row-template-button-design';

/**
 * Predefined template IDs for blog list pages.
 * Each template defines a different visual layout and behavior.
 */
export const DoppeBlogListPageTemplateId = {
    /**
     * Frameless template - clean grid layout without card borders.
     * Image and text displayed without container borders.
     * Uses responsive grid with min/max widths.
     */
    FramelessTemplate: 'FramelessTemplate',

    /**
     * Card template with bordered card containers.
     * Each blog post is displayed in a card with subtle borders.
     * Uses responsive grid with min/max widths.
     */
    CardTemplate: 'CardTemplate',

    /**
     * On-image template with text overlay on the blog post image.
     * Blog info is displayed on top of the image.
     * Fixed image ratio (not editable).
     */
    OnImageTemplate: 'OnImageTemplate',

    /**
     * Row template with horizontal list layout.
     * Each blog post is displayed as a full-width row.
     * Uses flex column layout (not grid).
     * Does not support custom image ratio.
     */
    RowTemplate: 'RowTemplate',

    // Legacy template IDs - kept for backward compatibility
    /** @deprecated Use FramelessTemplate instead */
    GridTemplate: 'GridTemplate',
    /** @deprecated Use RowTemplate instead */
    ListTemplate: 'ListTemplate',
} as const;

export type DoppeBlogListPageTemplateId = Values<typeof DoppeBlogListPageTemplateId>;

/**
 * Template kind for blog list pages.
 */
export const DoppeBlogListPageTemplateKind = {
    /**
     * A predefined template with a specific templateId.
     */
    Predefined: 'predefined',
} as const;

export type DoppeBlogListPageTemplateKind = Values<typeof DoppeBlogListPageTemplateKind>;

export const DoppeBlogImageRatio = DoppeActionLayoutMediaRatio;

export type DoppeBlogImageRatio = Values<typeof DoppeBlogImageRatio>;

/**
 * Template selector for blog list pages.
 * Specifies which template to use for rendering the page.
 */
export type DoppeBlogListPageTemplate = DoppeBlogListPageTemplate.Predefined;

export namespace DoppeBlogListPageTemplate {
    /**
     * A predefined template with a specific templateId.
     */
    export interface Predefined {
        kind: typeof DoppeBlogListPageTemplateKind.Predefined;
        templateId: DoppeBlogListPageTemplateId;
    }
}

/**
 * Template-specific props for each blog list page template.
 * Each template has its own set of configurable properties.
 */
export type DoppeBlogListPageTemplateProps = 
    | DoppeBlogListPageTemplateProps.FramelessTemplate
    | DoppeBlogListPageTemplateProps.CardTemplate
    | DoppeBlogListPageTemplateProps.OnImageTemplate
    | DoppeBlogListPageTemplateProps.RowTemplate
    // Legacy types for backward compatibility
    | DoppeBlogListPageTemplateProps.GridTemplate
    | DoppeBlogListPageTemplateProps.ListTemplate;

export namespace DoppeBlogListPageTemplateProps {
    /**
     * Props for the FramelessTemplate.
     * Clean grid layout without card borders.
     * Uses responsive grid with min/max widths.
     */
    export interface FramelessTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeBlogListPageTemplateId.FramelessTemplate;

        /**
         * Layout size for the blog posts grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;

        /**
         * Image aspect ratio for blog post images.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the CardTemplate.
     * Grid layout with bordered card containers.
     * Uses responsive grid with min/max widths.
     */
    export interface CardTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeBlogListPageTemplateId.CardTemplate;

        /**
         * Layout size for the blog posts grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;

        /**
         * Image aspect ratio for blog post images.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the OnImageTemplate.
     * Grid layout with text overlay on images.
     * Uses responsive grid with min/max widths based on layout size.
     * For Small layout size: only vertical ratios (1:1, 4:5, 3:4, 2:3, 9:16).
     * For Medium layout size: full range of ratios available.
     */
    export interface OnImageTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeBlogListPageTemplateId.OnImageTemplate;

        /**
         * Layout size for the blog posts grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;
        
        /**
         * Image aspect ratio for blog post images.
         * For Small layout size: only vertical ratios are available (1:1, 4:5, 3:4, 2:3, 9:16).
         * For Medium layout size: full range of ratios available.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the RowTemplate.
     * Horizontal list layout with flex column.
     * Does not support custom image ratio.
     */
    export interface RowTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeBlogListPageTemplateId.RowTemplate;
        
        /**
         * Whether to show the media/image in the row template.
         * When false, the image container is hidden and only text content is shown.
         */
        showMedia: boolean;
    }

    // Legacy types for backward compatibility
    /** @deprecated Use FramelessTemplate instead */
    export interface GridTemplate {
        layoutSize: DoppeProductsGridLayoutSize;
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /** @deprecated Use RowTemplate instead */
    export interface ListTemplate {
        layoutSize: DoppeProductsGridLayoutSize;
        imageRatio: DoppeActionLayoutMediaRatio;
    }
}

/**
 * Common props shared by all blog list page templates.
 * These props are template-agnostic and control shared behavior.
 */
export interface DoppeBlogListPageCommonProps {
    /**
     * Whether to show the blog post excerpt/description.
     * @default true
     */
    showExcerpt: boolean;

    /**
     * Whether to show the blog post date.
     * @default true
     */
    showDate: boolean;

    /**
     * Whether to show the reading time.
     * @default true
     */
    showReadingTime: boolean;

    /**
     * Whether to show the category badge.
     * @default true
     */
    showCategoryBadge: boolean;

    /**
     * Whether to show the Read More button on blog cards.
     * @default false
     */
    showReadMoreButton: boolean;

    /**
     * Custom text for the Read More button.
     * @default "Read More"
     */
    readMoreButtonText: string;

    /**
     * The visual design of the action button in row template.
     * - Icon: Textual link with arrow icon
     * - IconButton: Circular icon button
     * - Button: Full text button
     * - None: No button displayed
     * Only applicable to RowTemplate.
     * @default "button"
     */
    buttonDesign?: DoppeRowTemplateButtonDesign;
}

/**
 * Creates default template settings for a blog list page.
 * @param templateId - Optional template ID. Uses FramelessTemplate if not specified.
 */
export function doppeBlogListPageTemplateCreateDefault(
    templateId: DoppeBlogListPageTemplateId = DoppeBlogListPageTemplateId.FramelessTemplate
): DoppeBlogListPageTemplate {
    return {
        kind: DoppeBlogListPageTemplateKind.Predefined,
        templateId: templateId,
    };
}

/**
 * Creates default template props for a given template.
 */
export function doppeBlogListPageTemplatePropsCreateDefault(
    templateId: DoppeBlogListPageTemplateId
): DoppeBlogListPageTemplateProps {
    switch (templateId) {
        case DoppeBlogListPageTemplateId.FramelessTemplate:
            return {
                templateType: DoppeBlogListPageTemplateId.FramelessTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Ratio16By9,
            };
        case DoppeBlogListPageTemplateId.CardTemplate:
            return {
                templateType: DoppeBlogListPageTemplateId.CardTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Ratio16By9,
            };
        case DoppeBlogListPageTemplateId.OnImageTemplate:
            return {
                templateType: DoppeBlogListPageTemplateId.OnImageTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Square,
            };
        case DoppeBlogListPageTemplateId.RowTemplate:
            return {
                templateType: DoppeBlogListPageTemplateId.RowTemplate,
                showMedia: true,
            };
        // Legacy templates - return frameless defaults
        case DoppeBlogListPageTemplateId.GridTemplate:
        case DoppeBlogListPageTemplateId.ListTemplate:
        default:
            return {
                templateType: DoppeBlogListPageTemplateId.FramelessTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Ratio16By9,
            };
    }
}

/**
 * Creates default common props for a blog list page.
 */
export function doppeBlogListPageCommonPropsCreateDefault(): DoppeBlogListPageCommonProps {
    return {
        showExcerpt: true,
        showDate: true,
        showReadingTime: true,
        showCategoryBadge: true,
        showReadMoreButton: false,
        readMoreButtonText: 'Read More',
        buttonDesign: DoppeRowTemplateButtonDesign.Button,
    };
}

/**
 * Type guard to check if template props are for FramelessTemplate.
 */
export function doppeBlogListPageTemplatePropsIsFramelessTemplate(
    props: DoppeBlogListPageTemplateProps
): props is DoppeBlogListPageTemplateProps.FramelessTemplate {
    return 'templateType' in props && props.templateType === DoppeBlogListPageTemplateId.FramelessTemplate;
}

/**
 * Type guard to check if template props are for CardTemplate.
 */
export function doppeBlogListPageTemplatePropsIsCardTemplate(
    props: DoppeBlogListPageTemplateProps
): props is DoppeBlogListPageTemplateProps.CardTemplate {
    return 'templateType' in props && props.templateType === DoppeBlogListPageTemplateId.CardTemplate;
}

/**
 * Type guard to check if template props are for OnImageTemplate.
 */
export function doppeBlogListPageTemplatePropsIsOnImageTemplate(
    props: DoppeBlogListPageTemplateProps
): props is DoppeBlogListPageTemplateProps.OnImageTemplate {
    return 'templateType' in props && props.templateType === DoppeBlogListPageTemplateId.OnImageTemplate;
}

/**
 * Type guard to check if template props are for RowTemplate.
 */
export function doppeBlogListPageTemplatePropsIsRowTemplate(
    props: DoppeBlogListPageTemplateProps
): props is DoppeBlogListPageTemplateProps.RowTemplate {
    return 'templateType' in props && props.templateType === DoppeBlogListPageTemplateId.RowTemplate;
}

/**
 * Type guard to check if template props support layout size.
 * FramelessTemplate, CardTemplate, and OnImageTemplate support layout size.
 * RowTemplate does not support layout size.
 */
export function doppeBlogListPageTemplatePropsSupportsLayoutSize(
    props: DoppeBlogListPageTemplateProps
): props is DoppeBlogListPageTemplateProps.FramelessTemplate 
    | DoppeBlogListPageTemplateProps.CardTemplate 
    | DoppeBlogListPageTemplateProps.OnImageTemplate {
    return doppeBlogListPageTemplatePropsIsFramelessTemplate(props) ||
           doppeBlogListPageTemplatePropsIsCardTemplate(props) ||
           doppeBlogListPageTemplatePropsIsOnImageTemplate(props);
}

/**
 * Check if template props support image ratio setting.
 * Frameless, Card, and OnImage templates support custom image ratio.
 * Row templates have fixed image ratios.
 */
export function doppeBlogListPageTemplatePropsSupportsImageRatio(
    props: DoppeBlogListPageTemplateProps
): props is DoppeBlogListPageTemplateProps.FramelessTemplate 
    | DoppeBlogListPageTemplateProps.CardTemplate
    | DoppeBlogListPageTemplateProps.OnImageTemplate {
    return doppeBlogListPageTemplatePropsIsFramelessTemplate(props) ||
           doppeBlogListPageTemplatePropsIsCardTemplate(props) ||
           doppeBlogListPageTemplatePropsIsOnImageTemplate(props);
}


